// CreamPost.tsx — Editorial Cream knowledge cards, rendered in React.
//
// v3 (Erez, 2026-06-10 evening): FRAMELESS — no hairline borders boxing the
// composition; the cream paper itself is the canvas, with generous breathing
// room between masthead, headline and body. Pieces with produced slides
// render as a SWIPEABLE carousel (scroll-snap: trackpad/touch swipe, plus
// invisible click zones on the edges and coral dots below).
//
// Render priority per item (Backlog tab only — this component is used by
// BacklogView, not the Calendar):
//   image_urls (>1)              → swipeable slide carousel (the real post)
//   backlog_image_url ?? image_url → single produced render
//   neither                      → the React draft template ($0 placeholder)
//
// backlog_image_url (MJ-225) is a Backlog-ONLY staged render so design-system
// work can be reviewed on the Backlog wall without changing the Calendar
// (CalendarTile reads image_url directly).

import { useRef, useState } from "react";
import {
  BarChart3,
  Brain,
  CalendarClock,
  FileCheck2,
  Filter,
  GitBranch,
  GraduationCap,
  Handshake,
  Layers,
  ListChecks,
  Megaphone,
  Moon,
  Network,
  PenLine,
  Rocket,
  Send,
  Settings,
  ShieldCheck,
  TrendingUp,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { Item } from "./ContentMapPage";

// ── Editorial Cream tokens ───────────────────────────────────────────────────

const CREAM = {
  paper: "#F7F3E9",
  ink: "#33322D",
  inkSoft: "#6E6A5E",
  coral: "#C9512F",
  hairline: "#D9D2C0",
  serif: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
};

const PIECE_ICONS: Record<number, LucideIcon> = {
  1: Network,
  2: Layers,
  3: Users,
  4: GitBranch,
  5: Brain,
  6: TrendingUp,
  7: BarChart3,
  8: FileCheck2,
  9: ListChecks,
  10: Moon,
  11: PenLine,
  12: Filter,
  13: ShieldCheck,
  14: ListChecks,
  15: CalendarClock,
  16: Brain,
  17: Settings,
  18: Rocket,
  19: Handshake,
  20: Send,
};

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  "Marketing": Megaphone,
  "Sales": Handshake,
  "Operations": Settings,
  "Case Studies": FileCheck2,
  "Pilot to Production": Rocket,
  "Trust & Governance": ShieldCheck,
  "Educational / Frameworks": GraduationCap,
  "Benchmark / Data": BarChart3,
  "Distribution Assets": Send,
  "Knowledge Publication": Brain,
};

function pieceIcon(item: Item): LucideIcon {
  if (item.story_order && item.story_arc?.startsWith("Knowledge Set"))
    return PIECE_ICONS[item.story_order] ?? Brain;
  return CATEGORY_ICONS[item.category ?? ""] ?? PenLine;
}

function folio(item: Item): string {
  if (item.story_order) return `Nº ${String(item.story_order).padStart(2, "0")}`;
  return "·";
}

function oneLine(text: string | null, max = 110): string {
  if (!text) return "";
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

// ── Swipeable slide carousel ─────────────────────────────────────────────────

function SlideCarousel({ urls, title }: { urls: string[]; title: string }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [idx, setIdx] = useState(0);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };

  const go = (delta: number) => {
    const el = trackRef.current;
    if (!el) return;
    const next = Math.min(Math.max(idx + delta, 0), urls.length - 1);
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div style={{ position: "relative", aspectRatio: "1 / 1", overflow: "hidden" }}>
      <div
        ref={trackRef}
        onScroll={onScroll}
        style={{
          display: "flex",
          width: "100%",
          height: "100%",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
        }}
      >
        {urls.map((u, i) => (
          <img
            key={u}
            src={u}
            alt={`${title} — slide ${i + 1}`}
            loading="lazy"
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              flex: "0 0 100%",
              scrollSnapAlign: "center",
              display: "block",
            }}
          />
        ))}
      </div>

      {idx > 0 && (
        <span
          role="button"
          aria-label="Previous slide"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "20%", cursor: "w-resize" }}
        />
      )}
      {idx < urls.length - 1 && (
        <span
          role="button"
          aria-label="Next slide"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "20%", cursor: "e-resize" }}
        />
      )}

      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 6,
          pointerEvents: "none",
        }}
      >
        {urls.map((_, i) => (
          <span
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: i === idx ? CREAM.coral : "rgba(51,50,45,0.22)",
              transition: "background 0.2s",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── The card ─────────────────────────────────────────────────────────────────

export function CreamPost({ item }: { item: Item }) {
  // Produced slides win: swipeable carousel for multi-slide pieces.
  if (Array.isArray(item.image_urls) && item.image_urls.length > 1) {
    return <SlideCarousel urls={item.image_urls} title={item.title} />;
  }
  // Backlog-only staged render takes priority over the canonical image_url.
  const single = (item as { backlog_image_url?: string | null }).backlog_image_url ?? item.image_url;
  if (single) {
    return (
      <img
        src={single}
        alt={item.title}
        loading="lazy"
        style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" }}
      />
    );
  }

  // React draft — frameless: the cream is the canvas.
  const Icon = pieceIcon(item);
  const isCarousel = (item.content_type ?? "").toLowerCase() === "carousel";

  return (
    <div
      style={{
        aspectRatio: "1 / 1",
        background: CREAM.paper,
        position: "relative",
        overflow: "hidden",
        containerType: "inline-size",
        display: "flex",
        flexDirection: "column",
        padding: "11% 12%",
        boxSizing: "border-box",
      }}
    >
      {/* masthead row */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: "11%",
        }}
      >
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "2.8cqw",
            fontWeight: 600,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: CREAM.coral,
          }}
        >
          AI Operations
        </span>
        <span
          style={{
            fontFamily: CREAM.serif,
            fontStyle: "italic",
            fontSize: "3.5cqw",
            color: CREAM.inkSoft,
          }}
        >
          {folio(item)}
        </span>
      </div>

      {/* headline — serif at normal weight; air does the work */}
      <div
        style={{
          fontFamily: CREAM.serif,
          fontSize: "7.6cqw",
          fontWeight: 400,
          lineHeight: 1.26,
          letterSpacing: "-0.005em",
          color: CREAM.ink,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {item.title}
      </div>

      {/* short coral rule, generous air, then the insight line */}
      <div
        style={{
          width: "13%",
          borderTop: `1px solid ${CREAM.coral}`,
          margin: "9% 0 7%",
        }}
      />
      <div
        style={{
          fontFamily: CREAM.serif,
          fontStyle: "italic",
          fontSize: "4cqw",
          lineHeight: 1.55,
          color: CREAM.inkSoft,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {oneLine(item.description)}
      </div>

      {/* footer — quiet motif + format note, no rule above */}
      <div
        style={{
          marginTop: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Icon
          strokeWidth={1}
          style={{ width: "6.5cqw", height: "6.5cqw", color: CREAM.ink, opacity: 0.55 }}
        />
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "2.6cqw",
            fontWeight: 600,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: CREAM.inkSoft,
          }}
        >
          {isCarousel ? "Carousel" : "Single"}
        </span>
      </div>
    </div>
  );
}
